import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  adminTenantService,
  ListTenantsParams,
  CreateTenantDto,
  UpdateTenantDto,
  SuspendTenantRequest,
  UpdateTenantQuotasDto,
} from '@/services/admin-tenant.service'
import { awaitRefresh } from '@/lib/awaitRefresh'

// ============================================================================
// Query Keys
// ============================================================================

export const tenantKeys = {
  all: ['admin', 'tenants'] as const,
  lists: () => [...tenantKeys.all, 'list'] as const,
  list: (params?: ListTenantsParams) => [...tenantKeys.lists(), params] as const,
  details: () => [...tenantKeys.all, 'detail'] as const,
  detail: (id: string) => [...tenantKeys.details(), id] as const,
  members: (id: string) => [...tenantKeys.detail(id), 'members'] as const,
  resources: (id: string) => [...tenantKeys.detail(id), 'resources'] as const,
  billing: (id: string) => [...tenantKeys.detail(id), 'billing'] as const,
  usage: (id: string, days?: number) => [...tenantKeys.detail(id), 'usage', days ?? 30] as const,
}

// ============================================================================
// Queries
// ============================================================================

/**
 * Hook to list tenants with pagination and filters
 * Returns paginated list of TenantListDto with pagination metadata
 */
export function useTenantList(params?: ListTenantsParams) {
  return useQuery({
    queryKey: tenantKeys.list(params),
    // signal comes from TanStack Query: changing a filter mid-flight aborts the request
    // the new one supersedes, so a slow earlier response cannot land after it.
    queryFn: ({ signal }) => adminTenantService.listTenants(params, signal),
    staleTime: 30000, // 30 seconds
    // Keep the current rows on screen while the next page/filter loads. The page pairs
    // this with isFetching (spinner) and isPlaceholderData (dimming) — without it every
    // filter change replaced the table with skeletons.
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch summary metrics across all tenants in the system (unfiltered, limit 100)
 */
export function useTenantSummaryMetrics() {
  const summaryParams: ListTenantsParams = {
    page: 1,
    pageSize: 100,
  }
  return useQuery({
    queryKey: [...tenantKeys.lists(), 'summary-metrics'],
    queryFn: ({ signal }) => adminTenantService.listTenants(summaryParams, signal),
    staleTime: 30000, // 30 seconds
  })
}

/**
 * Hook to get tenant detail
 */
export function useTenantDetail(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.detail(tenantId),
    queryFn: () => adminTenantService.getTenantDetail(tenantId),
    enabled: !!tenantId,
    staleTime: 60000, // 1 minute
  })
}

/**
 * Hook to get tenant members
 */
export function useTenantMembers(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.members(tenantId),
    queryFn: () => adminTenantService.getTenantMembers(tenantId),
    enabled: !!tenantId,
  })
}

/**
 * Hook to get tenant resources
 */
export function useTenantResources(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.resources(tenantId),
    queryFn: () => adminTenantService.getTenantResources(tenantId),
    enabled: !!tenantId,
  })
}

/**
 * Hook to get tenant billing summary
 */
export function useTenantBilling(tenantId: string) {
  return useQuery({
    queryKey: tenantKeys.billing(tenantId),
    queryFn: () => adminTenantService.getTenantBillingSummary(tenantId),
    enabled: !!tenantId,
  })
}

/**
 * Hook to get tenant usage history
 */
export function useTenantUsageHistory(tenantId: string, days: number = 30) {
  return useQuery({
    queryKey: tenantKeys.usage(tenantId, days),
    queryFn: () => adminTenantService.getTenantUsageHistory(tenantId),
    enabled: !!tenantId,
  })
}

// ============================================================================
// Mutations
// ============================================================================
//
// Loading standard: every write invalidates from `onSettled`, not `onSuccess`, and RETURNS the
// promise. A mutation stays `isPending` until React Query has settled that callback, so the caller's
// pending flag — the spinner, the dialog held open, the page overlay — lifts only once the refetch
// has landed and the screen shows the new state. Closing on the bare API answer left the header badge
// and the quota figures stale under a dialog that had already gone, and a quick reopen offered
// "Suspend" again on a tenant that was already suspended. It runs on failure too: a request that
// timed out may still have been applied. A caller's own `mutate(…, { onSuccess })` runs after this.

/**
 * Hook to create a new tenant
 */
export function useCreateTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTenantDto) => adminTenantService.createTenant(data),
    onSuccess: () => {
      toast.success('Tenant created successfully')
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to create tenant')
    },
    onSettled: () => awaitRefresh(queryClient.invalidateQueries({ queryKey: tenantKeys.lists() })),
  })
}

/**
 * Hook to update tenant details
 */
export function useUpdateTenant(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateTenantDto) => adminTenantService.updateTenant(tenantId, data),
    onSuccess: () => {
      toast.success('Tenant updated successfully')
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update tenant')
    },
    onSettled: () =>
      awaitRefresh(Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) }),
        queryClient.invalidateQueries({ queryKey: tenantKeys.lists() }),
      ])),
  })
}

/**
 * Hook to suspend a tenant
 */
export function useSuspendTenant(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SuspendTenantRequest) => adminTenantService.suspendTenant(tenantId, data),
    onSuccess: () => {
      toast.success('Tenant suspended successfully')
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to suspend tenant')
    },
    onSettled: () =>
      awaitRefresh(Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) }),
        queryClient.invalidateQueries({ queryKey: tenantKeys.lists() }),
      ])),
  })
}

/**
 * Hook to activate a tenant
 */
export function useActivateTenant(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => adminTenantService.activateTenant(tenantId),
    onSuccess: () => {
      toast.success('Tenant activated successfully')
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to activate tenant')
    },
    onSettled: () =>
      awaitRefresh(Promise.all([
        queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) }),
        queryClient.invalidateQueries({ queryKey: tenantKeys.lists() }),
      ])),
  })
}

/**
 * Hook to update tenant quotas
 */
export function useUpdateQuotas(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateTenantQuotasDto) => adminTenantService.updateTenantQuotas(tenantId, data),
    onSuccess: () => {
      toast.success('Quotas updated successfully')
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update quotas')
    },
    onSettled: () => awaitRefresh(queryClient.invalidateQueries({ queryKey: tenantKeys.detail(tenantId) })),
  })
}

/**
 * Sends one tenant an expiry reminder listing every machine within seven days of expiry.
 *
 * The API answers 200 for "nothing was due" as well as for a send, so the toast reads `sent`
 * rather than the HTTP status: telling an operator "reminder sent" when nothing went out would
 * have them tell the customer to check an inbox that will stay empty. Either way the API's own
 * sentence is what gets shown — it names the recipient and the machine count, which no message
 * written here could.
 */
export function useSendTenantExpiryReminder() {
  return useMutation({
    mutationFn: (tenantId: string) => adminTenantService.sendExpiryReminder(tenantId),
    onSuccess: (result) => {
      if (result.sent) {
        toast.success(result.message)
      } else {
        toast.info(result.message)
      }
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to send the expiry reminder')
    },
  })
}
